import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { STATIC_PAGES } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { authFetch, getApiBase, getPathPrefix, resolveMediaUrl } from "../../utils/preview";
import "../styles/PublicCommon.css";
import "./WorksPage.css";

const LINE_URL = "https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx";

// APIから取得するデータの型定義
interface ResultCategory {
  id: string;
  name: string;
  description: string;
  display_order: number;
  thumbnail_url: string;
  is_enabled: boolean;
}

interface ResultMedia {
  name?: string;
  size?: number;
  content_type?: string;
  media_type?: string;
  storage_path: string;
  display_order?: number;
  caption?: string;
}

interface Result {
  id: string;
  title: string;
  category_id: string;
  completion_date: string;
  main_image: ResultMedia | null;
  gallery_images: ResultMedia[];
  videos: ResultMedia[];
  client_name: string;
  location: string;
  site_area: string;
  building_area: string;
  total_floor_area: string;
  structure_type: string;
  description: string;
  display_priority: number;
  is_published: boolean;
}

interface PublicResultsResponse {
  categories: ResultCategory[];
  results: Result[];
}

export const WorksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || "all");
  const [categories, setCategories] = useState<ResultCategory[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLinePopupOpen, setIsLinePopupOpen] = useState(false);
  const pathPrefix = getPathPrefix();

  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  const handleLineClick = useCallback(() => {
    if (isMobileDevice()) {
      window.open(LINE_URL, "_blank", "noopener,noreferrer");
    } else {
      setIsLinePopupOpen(true);
    }
  }, [isMobileDevice]);

  const closeLinePopup = useCallback(() => {
    setIsLinePopupOpen(false);
  }, []);

  // APIからデータを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await authFetch(`${getApiBase()}/results`);
        if (!response.ok) {
          throw new Error("データの取得に失敗しました");
        }
        const data: PublicResultsResponse = await response.json();
        setCategories(data.categories || []);
        setResults(data.results || []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "エラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // URLパラメータが変更されたら選択カテゴリを更新
  useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  // カテゴリ選択時にURLパラメータも更新
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  // フィルタリングされた実績
  const filteredWorks =
    selectedCategory === "all"
      ? results
      : results.filter((work) => work.category_id === selectedCategory);

  // カテゴリ名を取得する関数
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.name || "";
  };

  return (
    <>
      <SEOHead page={STATIC_PAGES.works} />

      <div className="public-page">
        <PublicHeader activePage="works" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* ページヘッダー */}
          <section className="page-header">
            <div className="page-header-overlay" />
            <div className="page-header-content">
              <h1 className="page-title">施工実績</h1>
              <p className="page-subtitle">Our Works</p>
            </div>
          </section>

          {/* フィルターセクション */}
          <section className="filter-section">
            <div className="container">
              <div className="category-filters" role="group" aria-label="カテゴリーフィルター">
                <button
                  className={`filter-button ${selectedCategory === "all" ? "active" : ""}`}
                  onClick={() => handleCategorySelect("all")}
                  aria-pressed={selectedCategory === "all"}
                >
                  すべて
                </button>
                {/* 中身（公開施工実績）が 1 件もないカテゴリはボタンを出さない */}
                {categories
                  .filter((c) => results.some((r) => r.category_id === c.id))
                  .map((category) => (
                    <button
                      key={category.id}
                      className={`filter-button ${
                        selectedCategory === category.id ? "active" : ""
                      }`}
                      onClick={() => handleCategorySelect(category.id)}
                      aria-pressed={selectedCategory === category.id}
                    >
                      {category.name}
                    </button>
                  ))}
              </div>
            </div>
          </section>

          {/* 実績一覧セクション */}
          <section className="works-section">
            <div className="container">
              {loading ? (
                <div className="loading-state">
                  <p>読み込み中...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                </div>
              ) : (
                <>
                  <div className="works-grid">
                    {filteredWorks.map((work) => (
                      <Link to={`${pathPrefix}/works/${work.id}`} key={work.id} className="work-card-link">
                        <article className="work-card">
                          <div className="work-image">
                            {work.main_image?.storage_path ? (
                              <img
                                src={resolveMediaUrl(work.main_image.storage_path)}
                                alt={`${work.title}の施工写真`}
                                className="work-image-img"
                              />
                            ) : (
                              <div
                                className="placeholder-image"
                                data-text={work.title}
                                role="img"
                                aria-label={`${work.title}の施工写真`}
                              />
                            )}
                            {work.category_id && (
                              <span className="work-category">
                                {getCategoryName(work.category_id)}
                              </span>
                            )}
                          </div>
                          <div className="work-content">
                            <h3 className="work-title">{work.title}</h3>
                            {work.description && (
                              <p className="work-description">{work.description}</p>
                            )}
                          </div>
                        </article>
                      </Link>
                    ))}
                  </div>

                  {filteredWorks.length === 0 && (
                    <div className="no-results">
                      <p>該当する施工実績が見つかりませんでした。</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* CTAセクション */}
          <section className="cta-section">
            <div className="container">
              <h2 className="cta-title">お見積もり・ご相談は無料です</h2>
              <p className="cta-text">
                お気軽にお問い合わせください。<br />
                お客様のご要望に合わせた最適なプランをご提案いたします。
              </p>
              <div className="cta-buttons">
                <a href="/contact" className="cta-button primary">
                  お問い合わせフォーム
                </a>
                <button type="button" className="cta-button line" data-track="line" onClick={handleLineClick}>
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <circle cx="12" cy="12" r="12" fill="#06C755" />
                    <path
                      d="M19.5 10.5c0-3.59-3.59-6.5-8-6.5s-8 2.91-8 6.5c0 3.21 2.85 5.9 6.7 6.41.26.06.62.18.71.41.08.21.05.54.03.75l-.12.7c-.03.21-.16.82.72.45.88-.38 4.74-2.79 6.47-4.78 1.19-1.31 1.76-2.64 1.76-4.04h-.27z"
                      fill="white"
                    />
                  </svg>
                  LINEで問い合わせ
                </button>
                <a href="tel:0894-62-4511" className="cta-button secondary">
                  <span>電話で問い合わせ</span>
                  <span className="phone-number">0894-62-4511</span>
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small-white.png"
                srcSet="/static/miyako-logo-small-white.png 1x, /static/miyako-logo-2x-white.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <a href="/">ホーム</a>
              <a href="/about">会社概要</a>
              <a href="/services">業務内容</a>
              <a href="/works">施工実績</a>
              <a href="/recruit">採用情報</a>
              <a href="/contact">お問い合わせ</a>
            </nav>

            <p className="footer-copyright">
              <small>
                ©2024 都建設株式会社 Powered by{" "}
                <a href="#" target="_blank" rel="noopener noreferrer">
                  VERYWEBB
                </a>
              </small>
            </p>
          </div>
        </footer>

        {/* LINE公式アカウント ポップアップ */}
        {isLinePopupOpen && (
          <>
            <div className="line-popup-overlay" onClick={closeLinePopup} aria-hidden="true" />
            <div className="line-popup" role="dialog" aria-modal="true" aria-labelledby="line-popup-title">
              <button
                className="line-popup-close"
                onClick={closeLinePopup}
                aria-label="閉じる"
                type="button"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
              <div className="line-popup-content">
                <h3 id="line-popup-title" className="line-popup-title">LINE公式アカウント</h3>
                <p className="line-popup-description">
                  お気軽にお問い合わせください。<br />
                  QRコードを読み取るか、下のボタンから友だち追加できます。
                </p>
                <div className="line-popup-qr">
                  <img
                    src="https://qr-official.line.me/gs/M_703jwzlx_GW.png"
                    alt="LINE公式アカウント QRコード"
                    width="180"
                    height="180"
                  />
                </div>
                <a
                  href={LINE_URL}
                  className="line-popup-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      d="M12 2C6.48 2 2 5.82 2 10.49c0 4.21 3.74 7.74 8.79 8.41.34.07.8.23.92.52.1.27.07.68.03.95l-.15.89c-.05.27-.21 1.05.92.57 1.13-.48 6.09-3.58 8.3-6.14C22.21 13.91 22 12.29 22 10.49 22 5.82 17.52 2 12 2z"
                      fill="white"
                    />
                  </svg>
                  友だち追加
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default WorksPage;
